import { Menu, Tooltip } from "antd";
import { MeterInstallationModeViewer } from "components/InstallationMode";
import MenuDropdown from "components/MenuDropdown";
import { InstallationModeContext } from "contexts/InstallationMode/installation-mode-context";
import useInstallationMode from "hooks/use-installation-mode";
import { MeterType, useUnmapMeterMutation } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useContext, useEffect, useState } from "react";
import { PositionFunction } from "types/InstallationMode.d";
import EditMeterModal from "./EditMeterModal";

export const MeteringPointsList: React.FC<{
  positions: any[];
  assets?: any[];
  streamMappings?: any[];
  roleAndPermission: any;
  refetch: () => void;
  onLocateMeter?: (measuredSubjects: { name: string; type: string; id: string }[]) => void;
}> = ({ positions, assets = [], streamMappings = [], roleAndPermission, refetch, onLocateMeter }) => {
  const [unmapMeter, { loading: unmappingMeter }] = useUnmapMeterMutation();
  const { setKeyPositionIds } = useInstallationMode();
  const { currentKeyEntryOfInstaller /* currentPositionId */ } = useContext(InstallationModeContext);

  // Edit modal state
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingMeterData, setEditingMeterData] = useState<any>(null);

  // Filter positions to get only metering points that have node slots
  const meteringPoints = positions.filter((pos: any) => {
    // Must be a metering point and not deleted
    if (pos.positionType !== "metering_point" || pos.deletedAt) {
      return false;
    }

    // Must have at least one node slot
    if (!pos.nodeSlots || pos.nodeSlots.length === 0) {
      return false;
    }

    return true;
  });

  // Register meter position IDs with installation mode context
  useEffect(() => {
    const meterPositionIds = meteringPoints.map((mp: any) => mp.positionId).filter(Boolean);
    if (meterPositionIds.length > 0) {
      setKeyPositionIds((prevIds: string[]) => {
        // Add meter position IDs if they're not already included
        const newIds = meterPositionIds.filter((id: string) => !prevIds.includes(id));
        if (newIds.length > 0) {
          return [...prevIds, ...newIds];
        }
        return prevIds;
      });
    }
  }, [meteringPoints, setKeyPositionIds]);

  // Helper function to get what this meter is measuring (asset or zone)
  const getMeasuredSubjects = (meteringPointPositionId: string) => {
    // Find all active stream mappings for this meter
    const meterMappings = streamMappings.filter(
      (mapping: any) => mapping.meteringPointPositionId === meteringPointPositionId && !mapping.decommissionedTime
    );

    if (meterMappings.length === 0) {
      return [];
    }

    // Get the subjects (assets or positions) that this meter is measuring
    const subjects = meterMappings.map((mapping: any) => {
      const { subjectId } = mapping;

      // First check if it's an asset
      const asset = assets.find((a: any) => a.id === subjectId);
      if (asset) {
        return {
          type: "asset",
          name: asset.description || asset.name || asset.type || "Unknown Asset",
          id: asset.id,
          channelIndex: mapping.channelIndex,
        };
      }

      // Then check if it's a position (zone)
      const position = positions.find((p: any) => p.positionId === subjectId);
      if (position) {
        return {
          type: "zone",
          name: position.positionName || "Unknown Zone",
          id: position.positionId,
          channelIndex: mapping.channelIndex,
        };
      }

      // If neither found, show the ID
      return {
        type: "unknown",
        name: `Unknown (${subjectId.substring(0, 8)}...)`,
        id: subjectId,
        channelIndex: mapping.channelIndex,
      };
    });

    // Sort by channel index for consistent display
    return subjects.sort((a, b) => a.channelIndex - b.channelIndex);
  };

  // Helper function to format the measured subjects display
  const formatMeasuredSubjects = (subjects: any[]) => {
    if (subjects.length === 0) {
      return "Not measuring anything";
    }

    if (subjects.length === 1) {
      const subject = subjects[0];
      return `${subject.name}`;
    }

    // Multiple subjects - group by type
    const assetCount = subjects.filter((s) => s.type === "asset").length;
    const zoneCount = subjects.filter((s) => s.type === "zone").length;
    const unknownCount = subjects.filter((s) => s.type === "unknown").length;

    const parts = [];
    if (assetCount > 0) parts.push(`${assetCount} asset${assetCount > 1 ? "s" : ""}`);
    if (zoneCount > 0) parts.push(`${zoneCount} zone${zoneCount > 1 ? "s" : ""}`);
    if (unknownCount > 0) parts.push(`${unknownCount} unknown`);

    return parts.join(", ");
  };

  // Helper function to get detailed tooltip for measured subjects
  const getMeasuredSubjectsTooltip = (subjects: any[]) => {
    if (subjects.length === 0) {
      return "This meter is not currently measuring any assets or zones";
    }

    if (subjects.length === 1) {
      const subject = subjects[0];
      return `Measuring: ${subject.name} (Channel ${subject.channelIndex + 1})`;
    }

    // Multiple subjects - show detailed list
    const lines = subjects.map((subject) => `• ${subject.name} (Channel ${subject.channelIndex + 1})`);
    return `Measuring:\n${lines.join("\n")}`;
  };

  // Helper function to get meter type from nodeSlots
  const getMeterType = (position: any) => {
    if (position.nodeSlots && position.nodeSlots.length > 0) {
      // Get the first active node slot (not removed)
      const activeSlot = position.nodeSlots.find((slot: any) => !slot.slotRemoveDate);
      if (activeSlot && activeSlot.nodeType) {
        return activeSlot.nodeType;
      }
      // If no active slot, get the most recent one
      if (position.nodeSlots[0] && position.nodeSlots[0].nodeType) {
        return position.nodeSlots[0].nodeType;
      }
    }
    return "Unknown";
  };

  // Helper function to get meter identifier from nodeSlots
  const getMeterIdentifier = (position: any) => {
    if (position.nodeSlots && position.nodeSlots.length > 0) {
      // Get the first active node slot (not removed)
      const activeSlot = position.nodeSlots.find((slot: any) => !slot.slotRemoveDate);
      if (activeSlot) {
        // For CT meters, use nodeMacId; for camera meters, use serialNumber
        // Check if the slot has mappings to determine the identifier
        if (activeSlot.slotMappings && activeSlot.slotMappings.length > 0) {
          const activeMapping = activeSlot.slotMappings.find((mapping: any) => !mapping.decomissionedTime);
          if (activeMapping && activeMapping.nodeMacId) {
            return activeMapping.nodeMacId;
          }
        }
        // Fallback to slot id if no mapping found
        return activeSlot.id;
      }
      // If no active slot, get the most recent one
      if (position.nodeSlots[0]) {
        if (position.nodeSlots[0].slotMappings && position.nodeSlots[0].slotMappings.length > 0) {
          const mapping = position.nodeSlots[0].slotMappings[0];
          if (mapping && mapping.nodeMacId) {
            return mapping.nodeMacId;
          }
        }
        return position.nodeSlots[0].id;
      }
    }
    return null;
  };

  // Helper function to get GraphQL meter type enum
  const getGraphQLMeterType = (position: any): MeterType => {
    if (position.nodeSlots && position.nodeSlots.length > 0) {
      // Get the first active node slot (not removed)
      const activeSlot = position.nodeSlots.find((slot: any) => !slot.slotRemoveDate);
      const slot = activeSlot || position.nodeSlots[0];

      if (slot) {
        // Primary check: Use MAC ID pattern - CT meters start with FFFF, camera meters don't
        const identifier = getMeterIdentifier(position);
        if (identifier && !identifier.toUpperCase().startsWith("FFFF")) {
          return MeterType.Camera;
        }

        // Secondary check: Use subType field if available
        if (slot.subType === "camera") {
          return MeterType.Camera;
        }

        // Additional check: Milesight devices are Camera meters, not CT meters
        // This handles legacy data where subType might not be set properly
        if (position.positionName && position.positionName.toLowerCase().includes("milesight")) {
          return MeterType.Camera;
        }

        // Check for CT meter (has nodeType: 'energy' and MAC ID starts with FFFF)
        if (slot.nodeType === "energy") {
          return MeterType.Ct;
        }
      }
    }
    // Default to CT for backward compatibility
    return MeterType.Ct;
  };

  // Helper function to determine if meter is mapped
  const isMeterMapped = (position: any) => {
    if (position.nodeSlots && position.nodeSlots.length > 0) {
      // Check if any slot is active (not removed)
      return position.nodeSlots.some((slot: any) => {
        // Slot is considered mapped if it exists and is not removed
        return !slot.slotRemoveDate;
      });
    }
    return false;
  };

  // Helper function to check if this is a CT meter (supports installation mode)
  const isCTMeter = (position: any) => {
    if (position.nodeSlots && position.nodeSlots.length > 0) {
      // Get the first active node slot (not removed)
      const activeSlot = position.nodeSlots.find((slot: any) => !slot.slotRemoveDate);
      const slot = activeSlot || position.nodeSlots[0];

      if (slot) {
        // Primary check: Use MAC ID pattern - CT meters start with FFFF, camera meters don't
        const identifier = getMeterIdentifier(position);
        if (identifier && !identifier.toUpperCase().startsWith("FFFF")) {
          return false; // Camera meter
        }

        // Secondary check: Camera meters have subType: 'camera' - these should NOT show installation mode
        if (slot.subType === "camera") {
          return false;
        }

        // Additional check: Milesight devices are Camera meters, not CT meters
        // This handles legacy data where subType might not be set properly
        if (position.positionName && position.positionName.toLowerCase().includes("milesight")) {
          return false;
        }

        // Only CT meters (nodeType: 'energy' and MAC ID starts with FFFF) should show installation mode
        return slot.nodeType === "energy";
      }
    }
    return false;
  };

  // Helper function to check if meter is in installation mode
  const isMeterInInstallationMode = (positionId: string) => {
    return (
      currentKeyEntryOfInstaller?.keyPositionId === positionId &&
      currentKeyEntryOfInstaller?.positionFunction === PositionFunction.METERING_POINT
    );
  };

  // Handler for editing meters
  const handleEditMeter = (position: any) => {
    const identifier = getMeterIdentifier(position);
    const meterType = getGraphQLMeterType(position);

    if (!identifier) {
      import("antd").then(({ message }) => {
        message.error("No meter identifier found for this position");
      });
      return;
    }

    // Get stream mappings for this meter
    const meterStreamMappings = streamMappings.filter(
      (mapping: any) => mapping.meteringPointPositionId === position.positionId && !mapping.decommissionedTime
    );

    const streams = meterStreamMappings.map((mapping: any) => ({
      subjectId: mapping.subjectId,
      channelIndex: mapping.channelIndex,
    }));

    setEditingMeterData({
      meteringPointPositionId: position.positionId,
      positionName: position.positionName,
      meterType,
      identifier,
      // CT clamp type will be fetched from position configuration in the edit modal
      currentCtClampType: undefined,
      streamMappings: streams,
      // Pass raw position data for additional meter type checking
      rawPositionData: position,
    });

    setIsEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setEditingMeterData(null);
  };

  const handleUnmapMeter = async (position: any) => {
    const identifier = getMeterIdentifier(position);
    const meterType = getGraphQLMeterType(position);

    if (!identifier) {
      import("antd").then(({ message }) => {
        message.error("No meter identifier found for this position");
      });
      return;
    }

    import("antd").then(({ Modal, message }) => {
      Modal.confirm({
        title: "Unmap Meter",
        content: `Are you sure you want to unmap the ${meterType.toLowerCase()} meter from '${position.positionName}'?`,
        okText: "Unmap",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            const result = await unmapMeter({
              variables: {
                meteringPointPositionId: position.positionId,
                meterType: meterType as any,
                identifier,
              },
            });

            if (result.data?.unmapMeter?.success) {
              message.success(`${meterType} meter unmapped successfully!`);
              refetch();
            } else {
              throw new Error(result.data?.unmapMeter?.message || "Failed to unmap meter");
            }
          } catch (err: any) {
            message.error(err.message || "Failed to unmap meter");
          }
        },
      });
    });
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "none",
        borderRadius: 0,
        padding: 0,
        minWidth: 0,
        maxWidth: "none",
        boxShadow: "none",
      }}
    >
      {meteringPoints.length === 0 ? (
        <div style={{ color: "#aaa", padding: 12 }}>No meters</div>
      ) : (
        meteringPoints.map((mp: any) => {
          const meteringPointId = mp.id || mp.meteringPointId || mp.positionId;
          const displayType = getMeterType(mp);
          const isMapped = isMeterMapped(mp);
          const isCtMeter = isCTMeter(mp);
          const isInInstallationMode = isMeterInInstallationMode(mp.positionId);
          const measuredSubjects = getMeasuredSubjects(mp.positionId);
          const formattedSubjects = formatMeasuredSubjects(measuredSubjects);
          const measuredSubjectsTooltip = getMeasuredSubjectsTooltip(measuredSubjects);
          return (
            <div
              key={mp.positionId}
              style={{
                margin: 12,
                marginBottom: 8,
                padding: 8,
                border: "1px solid #d9d9d9",
                borderRadius: 4,
                background: "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{mp.positionName}</div>
                <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                  <span style={{ fontWeight: 500 }}>Type:</span> {displayType}
                  {isMapped && !isInInstallationMode && (
                    <span style={{ marginLeft: 8, color: "#52c41a" }}>• Mapped</span>
                  )}
                  {!isMapped && <span style={{ marginLeft: 8, color: "#faad14" }}>• Not Mapped</span>}
                  {isInInstallationMode && (
                    <span style={{ marginLeft: 8, color: "#1890ff", fontWeight: 500 }}>• Installation Mode</span>
                  )}
                </div>
                <Tooltip title={measuredSubjectsTooltip} placement="topLeft">
                  <div style={{ fontSize: 11, color: "#666", marginTop: 2, cursor: "help" }}>
                    <span style={{ fontWeight: 500 }}>Measuring:</span> {formattedSubjects}
                  </div>
                </Tooltip>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Installation Mode Viewer for CT meters */}
                {roleAndPermission.isPC() && meteringPointId && isMapped && isCtMeter && (
                  <MeterInstallationModeViewer
                    keyPositionId={mp.positionId}
                    meterIdentifier={getMeterIdentifier(mp) || ""}
                    meterType={getGraphQLMeterType(mp)}
                    roleAndPermission={roleAndPermission}
                  />
                )}

                {/* Menu Dropdown for meter actions */}
                <MenuDropdown
                  menu={
                    <Menu
                      style={{ minWidth: 160 }}
                      items={[
                        {
                          key: "locate",
                          label: "Locate",
                          onClick: () => onLocateMeter?.(measuredSubjects),
                        },
                        ...(roleAndPermission.isPC() && meteringPointId && isMapped
                          ? [
                              {
                                type: "divider" as const,
                              },
                              {
                                key: "edit",
                                label: "Edit Meter",
                                onClick: () => handleEditMeter(mp),
                              },
                              {
                                key: "unmap",
                                label: "Unmap Meter",
                                danger: true,
                                onClick: () => handleUnmapMeter(mp),
                                disabled: unmappingMeter,
                              },
                            ]
                          : []),
                      ]}
                    />
                  }
                />
              </div>
            </div>
          );
        })
      )}

      {/* Edit Meter Modal */}
      <EditMeterModal
        visible={isEditModalVisible}
        onClose={handleCloseEditModal}
        locationId={positions[0]?.locationId || ""} // Get locationId from first position
        meterData={editingMeterData}
        refetch={refetch}
      />
    </div>
  );
};

export default MeteringPointsList;
